import * as models from 'smg/customer-api/models';
import { DollarFormat } from 'smg/util';

import i18n from '../i18n';

import { PaymentCommitted } from './payment/plan';
import { StartSplitFromResume } from './payment/split';
import { State } from './state';

// Resume wires an in-flight order the cart bootstrap found for this cart
// (#7956/#8117). A reload after a card decline, network blip, or PayPal
// cancel round-trip loses the page-local orderResp that submit.ts's
// `state.orderResp ? resolve : createOrder` guard relies on; seeding it
// from cart.resumableOrder makes Place Order skip creation and pay the
// existing order instead of duplicating it.

let $resumeOrderCard: HTMLElement | null;
let $resumeOrderMessage: HTMLElement | null;
let $resumeOrderShipping: HTMLElement | null;
let $resumeStartOver: HTMLButtonElement | null;

export function SetupResume(state: State): void {
    $resumeOrderCard = document.querySelector('#resume-order-card');
    $resumeOrderMessage = document.querySelector('#resume-order-message');
    $resumeOrderShipping = document.querySelector('#resume-order-shipping');
    $resumeStartOver = document.querySelector('#resume-start-over');

    $resumeStartOver?.addEventListener('click', e => {
        e.preventDefault();
        // Money may already have moved on this order; starting over would drop that charge and let a new order
        // charge the same cart again.
        if (PaymentCommitted(state)) {
            return;
        }

        state.orderResp = undefined;
        state.split = undefined;
        state.resumeAuthoritativeTotal = undefined;
        state.detailsComplete = false;
        state.paymentStepComplete = false;
        // The summary must repaint with live semantics — the abandoned order
        // keeps the spent balance, so the new order will not get it.
        void state.updateOrderSummary();
        // Payment may already have been confirmed during the resume attempt;
        // without this the authorize button would let them submit a fresh
        // order without walking the additional-details step.
        state.dom.$authorizePaymentBtn.disabled = true;

        $resumeOrderCard?.classList.add('d-none');
        state.dom.$shippingBodyToggle?.classList.remove('d-none');
        // Reopening shipping also re-reveals #section-additional via the
        // existing shown.bs.collapse handler, so the customer walks the
        // normal flow forward and a fresh order gets created at Place Order.
        state.dom.$shippingBodyToggle?.click();
    });
}

export async function ApplyResumableOrder(
    state: State,
    resumableOrder?: models.CartSummaryResponse['resumableOrder'],
): Promise<boolean> {
    if (!resumableOrder?.orderID) {
        return false;
    }

    state.orderResp = new models.OrderCreateResponse({
        orderID: resumableOrder.orderID,
        key: resumableOrder.key,
        invoiceNumber: resumableOrder.invoiceNumber,
    });
    state.detailsComplete = true;
    const splitResume = await StartSplitFromResume(state, resumableOrder);

    state.shippingOption = new models.ShippingOption(resumableOrder.shippingOption);
    const resumedAddress = state.shippingOption.address;
    state.customer = await state.getUser();

    // A zero/absent exemption means the customer answered "Not Exempt" —
    // leave state.taxExemption undefined rather than inventing a blank.
    if (resumableOrder.userTaxExemption?.id) {
        state.taxExemption = new models.UserTaxExemption(resumableOrder.userTaxExemption);
    }

    // Resume never opens shipping, so the shown.bs.collapse handler that
    // normally un-hides sections never fires — mirror it here, BEFORE the
    // summary updates so updateShippingSummary can re-hide the tax section
    // for a non-US order. Deliberately leave section-additional hidden:
    // those answers are already baked into the created order and can't be
    // re-submitted.
    const { dom } = state;
    dom.$spinner.classList.add('d-none');
    dom.$sectionShipping?.classList.remove('d-none');
    dom.$sectionTax?.classList.remove('d-none');
    dom.$sectionPayment?.classList.remove('d-none');
    dom.$orderSummaryColumn?.classList.remove('d-none');

    // updateOrderSummary re-fetches the cart with the shipping tax address so
    // totals match what the order was priced at.
    await state.updateOrderSummary();
    state.updateShippingSummary();
    await state.updateTaxExemptionSummary();

    // Lock settled shipping; tax edit toggle already starts d-none and stays
    // that way since taxStepComplete remains false.
    dom.$shippingBodyToggle?.classList.add('d-none');

    if ($resumeOrderMessage) {
        if (splitResume && state.resumeAuthoritativeTotal) {
            const paid = state.resumeAuthoritativeTotal.minus(resumableOrder.balance);
            $resumeOrderMessage.textContent = i18n('checkoutSplitResume', {
                invoice: String(resumableOrder.invoiceNumber || ''),
                paid: DollarFormat(paid),
                total: DollarFormat(state.resumeAuthoritativeTotal),
                left: DollarFormat(resumableOrder.balance),
            });
            const paidBlock = document.querySelector('#resume-order-paid');
            paidBlock?.classList.remove('d-none');
            paidBlock?.classList.add('d-flex');
            const paidAmount = document.querySelector('#resume-order-paid-amount');
            const leftAmount = document.querySelector('#resume-order-left-amount');
            const bar = document.querySelector('#resume-order-paid-bar');
            if (paidAmount) {
                paidAmount.textContent = DollarFormat(paid);
            }
            if (leftAmount) {
                leftAmount.textContent = DollarFormat(resumableOrder.balance);
            }
            if (bar instanceof HTMLElement && state.resumeAuthoritativeTotal.gt(0)) {
                const pct = Math.min(100, Math.round(paid.div(state.resumeAuthoritativeTotal).times(100).toNumber()));
                bar.style.width = `${pct}%`;
                bar.parentElement?.setAttribute('aria-valuenow', String(pct));
            }
            $resumeStartOver?.classList.add('d-none');
        } else {
            $resumeOrderMessage.textContent = resumableOrder.invoiceNumber ?
                `You have an unfinished order #${resumableOrder.invoiceNumber} for this cart. Complete your payment below.` :
                'You have an unfinished order for this cart. Complete your payment below.';
        }
    }
    if ($resumeOrderShipping) {
        $resumeOrderShipping.textContent = resumedAddress?.fullName ?
            `Shipping to ${resumedAddress.fullName}` :
            '';
    }
    $resumeOrderCard?.classList.remove('d-none');

    // Open payment; bindPaymentCollapse runs initPayment on the toggle click.
    state.dom.$paymentBodyToggle?.click();
    return true;
}
